import type { ChoiceDuelState, PlayerIndex } from "@playpal/core";
import { useEffect, useRef, useState } from "react";
import { playTones } from "../lib/sound";

interface Props {
	state: ChoiceDuelState;
	seat: PlayerIndex;
	onMove: (move: unknown) => void;
	/** Server clock minus this device's clock. */
	clockOffset: number;
}

/**
 * One screen for the whole "both answer at once" family — times tables,
 * spelling, sorting, geography, ear training, would-you-rather. A new game in
 * that family needs no UI work at all.
 */
export function ChoiceDuelBoard({ state, seat, onMove, clockOffset }: Props) {
	const round = state.rounds[state.index];
	const myAnswer = state.answers[seat];
	const theirAnswer = state.answers[seat === 0 ? 1 : 0];
	const revealing = state.phase === "reveal";
	const opinionRound = round?.answer === -1 && revealing;

	const remaining = useCountdown(state.endsAt, clockOffset, state.phase === "question");
	const fraction = Math.max(0, Math.min(1, remaining / Math.max(1, state.questionMs)));

	// Ear-training rounds play their tones as soon as they appear.
	const played = useRef<string | null>(null);
	useEffect(() => {
		if (!round?.tones || played.current === round.id) return;
		played.current = round.id;
		playTones(round.tones.split(",").map(Number));
	}, [round?.tones, round?.id]);

	if (!round) return null;

	return (
		<div className="board-frame board-2xl">
			<div className="mb-3 flex items-center justify-between text-sm font-semibold text-indigo-900/70 dark:text-indigo-100/70">
				<span>
					Round {state.index + 1} of {state.rounds.length}
				</span>
				<span>
					{state.scores[0]} – {state.scores[1]}
				</span>
			</div>

			<div
				className="mb-4 h-3 overflow-hidden rounded-full bg-white/60 dark:bg-white/10"
				role="timer"
				aria-label={`${Math.ceil(remaining / 1000)} seconds left`}
			>
				<div
					className={`h-full rounded-full transition-[width] duration-200 ease-linear ${
						fraction < 0.25 ? "bg-rose-500" : "bg-emerald-500"
					}`}
					style={{ width: `${revealing ? 100 : fraction * 100}%` }}
				/>
			</div>

			{round.glyph && (
				<p className="mb-2 text-center text-6xl" aria-hidden="true">
					{round.glyph}
				</p>
			)}
			<h3 className="mb-5 text-center text-2xl font-black sm:text-3xl">{round.prompt}</h3>

			{round.tones && (
				<div className="mb-4 text-center">
					<button
						type="button"
						onClick={() => playTones((round.tones ?? "").split(",").map(Number))}
						className="rounded-2xl bg-white/80 px-5 py-3 text-lg font-bold shadow-md dark:bg-white/10"
					>
						🔊 Play again
					</button>
				</div>
			)}

			<div className="grid gap-3 sm:grid-cols-2">
				{round.choices.map((choice, index) => {
					const chosenByMe = myAnswer?.choice === index;
					const chosenByThem = revealing && theirAnswer?.choice === index;
					const correct = revealing && round.answer === index;
					const wrongPick = revealing && chosenByMe && round.answer >= 0 && !correct;

					return (
						<button
							key={choice}
							type="button"
							disabled={revealing || myAnswer !== null}
							onClick={() => onMove({ choice: index })}
							className={`rounded-2xl px-5 py-4 text-left text-xl font-bold shadow-md transition sm:text-2xl ${
								correct
									? "bg-emerald-400 text-emerald-950"
									: wrongPick
										? "bg-rose-400 text-rose-950"
										: chosenByMe
											? "bg-indigo-600 text-white"
											: "bg-white/80 enabled:hover:scale-[1.02] dark:bg-white/10"
							}`}
						>
							<span>{choice}</span>
							{(chosenByMe || chosenByThem) && (
								<span className="ml-2 text-base font-semibold opacity-80">
									{chosenByMe ? "you" : ""}
									{chosenByMe && chosenByThem ? " + " : ""}
									{chosenByThem ? "them" : ""}
								</span>
							)}
						</button>
					);
				})}
			</div>

			<p className="mt-5 text-center text-lg font-semibold" aria-live="polite">
				{revealing
					? opinionRound
						? state.lastAward[seat] > 0
							? "You both picked the same! +1 each 🎉"
							: "You picked differently this time 🤷"
						: describeAward(state.lastAward[seat], round.note)
					: myAnswer
						? theirAnswer
							? "Both in!"
							: "Locked in — waiting for your friend…"
						: "Pick an answer, quick!"}
			</p>
		</div>
	);
}

function describeAward(points: number, note?: string): string {
	const suffix = note ? ` (${note})` : "";
	if (points >= 2) return `Correct — and fastest! +2 points 🏆${suffix}`;
	if (points === 1) return `Correct! +1 point ✅${suffix}`;
	return `Not this time — next one coming up…${suffix}`;
}

/** Ticks against the server's clock, not the device's. */
function useCountdown(endsAt: number, clockOffset: number, running: boolean): number {
	const [remaining, setRemaining] = useState(() => endsAt - (Date.now() + clockOffset));

	useEffect(() => {
		if (!running) return;
		const update = () => setRemaining(endsAt - (Date.now() + clockOffset));
		update();
		const timer = setInterval(update, 250);
		return () => clearInterval(timer);
	}, [endsAt, clockOffset, running]);

	return Math.max(0, remaining);
}
